/*  Colega Link · Detalle público del Abogado
    Ruta: /app/abogados/:id
    Diseño espejo de MediatorDetail.js pero enfocado en abogados:
    - Header con avatar, nombre, insignias (verificado, Pionero, Embajador)
    - Especialidades, bio, ubicación
    - Rating promedio + lista completa de reseñas
    - Sidebar de reputación + trust panel
*/
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { BadgeCheck, MapPin, Star, ChevronLeft, Lock, ShieldCheck, Crown, Trophy, Briefcase, CheckCircle2 } from "lucide-react";
import Avatar from "@/components/Avatar";

const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("es-CL", { day: "numeric", month: "short", year: "numeric" });
  } catch { return ""; }
};

export default function LawyerDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: d } = await api.get(`/lawyers/${id}`);
        if (!cancelled) setData(d);
      } catch (err) {
        if (!cancelled) setError(formatApiError(err.response?.data?.detail) || err.message);
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  if (error) {
    return (
      <div className="container-app py-16" data-testid="lawyer-detail-error">
        <button onClick={() => nav(-1)} className="text-sm inline-flex items-center text-muted-foreground hover:text-foreground mb-6">
          <ChevronLeft size={14}/>Volver
        </button>
        <div className="panel p-8 text-[hsl(var(--destructive))]">{error}</div>
      </div>
    );
  }
  if (!data) return <div className="container-app py-16 text-muted-foreground" data-testid="lawyer-detail-loading">Cargando…</div>;
  const l = data.lawyer;

  const isPionero = l.plan === "pionero";
  const isEmbajador = l.plan === "embajador_pionero";
  const pioneroNum = l.pionero_number || l.founder_number;
  const stars = Number(l.reputation || 0);

  return (
    <div className="container-app py-12" data-testid="lawyer-detail">
      <button onClick={() => nav(-1)} className="text-sm inline-flex items-center text-muted-foreground hover:text-foreground" data-testid="back-btn">
        <ChevronLeft size={14}/>Volver
      </button>

      <div className="mt-6 grid lg:grid-cols-3 gap-10">
        {/* Columna principal */}
        <div className="lg:col-span-2">
          <div className="flex items-start gap-5 flex-wrap">
            <Avatar
              name={l.name}
              src={l.has_avatar ? l.avatar_url : null}
              size={88}
              testid="lawyer-detail-avatar"
            />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap" data-testid="lawyer-badges">
                {l.verified && <span className="pill badge-verified"><BadgeCheck size={12}/>Verificado</span>}
                {l.registry_number && <span className="pill">{l.registry_number}</span>}
                {isEmbajador && (
                  <span className="pill inline-flex items-center gap-1 bg-emerald-400/15 border-emerald-400/40 text-emerald-200">
                    <Trophy size={12}/> Pionero Fundador
                  </span>
                )}
                {isPionero && (
                  <span className="pill inline-flex items-center gap-1 bg-amber-400/15 border-amber-400/40 text-amber-200">
                    <Crown size={12}/> Pionero{pioneroNum ? ` N°${pioneroNum}` : ""}
                  </span>
                )}
                <span className="pill badge-cyan"><Lock size={12}/>Contacto reservado</span>
              </div>
              <h1 className="serif text-4xl md:text-5xl mt-3" data-testid="lawyer-name">{l.name}</h1>
              <div className="mt-2 text-muted-foreground inline-flex items-center gap-1.5">
                <MapPin size={14}/>{l.comuna || "—"}{l.region ? `, ${l.region}` : ""}
              </div>
            </div>
          </div>

          {/* Bio */}
          {l.bio && (
            <p className="mt-6 text-foreground/90 leading-relaxed max-w-2xl whitespace-pre-line" data-testid="lawyer-bio">
              {l.bio}
            </p>
          )}

          {/* Especialidades */}
          {(l.specialties || []).length > 0 && (
            <div className="mt-6" data-testid="lawyer-specialties">
              <div className="label-eyebrow mb-2">Especialidades</div>
              <div className="flex flex-wrap gap-2">
                {(l.specialties || []).map((s) => <span key={s} className="pill">{s}</span>)}
              </div>
            </div>
          )}

          {/* Contadores de autoridad — sólo visibles cuando (A + B) ≥ 3 */}
          {((data.authority_counters?.total) || 0) >= 3 && (
            <div className="mt-8 grid sm:grid-cols-2 gap-3" data-testid="lawyer-authority-counters">
              <div className="panel-glow p-5 border-cyan-400/30">
                <div className="flex items-center gap-3">
                  <Briefcase size={20} className="text-cyan-300 shrink-0"/>
                  <div>
                    <div className="serif text-3xl leading-none" data-testid="lawyer-delegations-published">
                      {data.authority_counters.delegations_published}
                    </div>
                    <div className="text-sm text-cyan-200/90 mt-1">Gestiones delegadas</div>
                  </div>
                </div>
              </div>
              <div className="panel-glow p-5 border-emerald-400/30">
                <div className="flex items-center gap-3">
                  <CheckCircle2 size={20} className="text-emerald-300 shrink-0"/>
                  <div>
                    <div className="serif text-3xl leading-none" data-testid="lawyer-delegations-fulfilled">
                      {data.authority_counters.delegations_fulfilled}
                    </div>
                    <div className="text-sm text-emerald-200/90 mt-1">Gestiones cumplidas</div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Reseñas */}
          <h2 className="serif text-3xl mt-12" data-testid="reviews-heading">
            Reseñas ({data.reviews?.length || 0})
          </h2>
          <ul className="mt-4 space-y-3" data-testid="reviews-list">
            {(!data.reviews || data.reviews.length === 0) && (
              <li className="panel p-6 text-muted-foreground" data-testid="reviews-empty">
                Aún no hay reseñas. Este abogado empieza con una hoja en blanco.
              </li>
            )}
            {(data.reviews || []).map((r) => (
              <li key={r.id} className="panel p-5" data-testid={`review-${r.id}`}>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-3">
                    <span className="font-medium">{r.author_name || "Anónimo"}</span>
                    <span className="inline-flex items-center gap-1 text-sm" data-testid={`review-rating-${r.id}`}>
                      {[1,2,3,4,5].map((n) => (
                        <Star
                          key={n}
                          size={13}
                          className={n <= r.rating ? "text-amber-400" : "text-muted-foreground/25"}
                          fill={n <= r.rating ? "currentColor" : "none"}
                        />
                      ))}
                    </span>
                  </div>
                  {r.created_at && (
                    <span className="text-[11px] text-muted-foreground">{fmtDate(r.created_at)}</span>
                  )}
                </div>
                {r.comment && (
                  <p className="mt-3 text-sm text-foreground/85 leading-relaxed whitespace-pre-line" data-testid={`review-comment-${r.id}`}>
                    {r.comment}
                  </p>
                )}
              </li>
            ))}
          </ul>
        </div>

        {/* Sidebar */}
        <aside className="space-y-4">
          <div className="panel p-6" data-testid="reputation-card">
            <div className="label-eyebrow">Reputación</div>
            <div className="serif text-5xl mt-2 inline-flex items-center gap-2">
              <Star size={24} className="text-amber-400" fill="currentColor"/>
              <span data-testid="reputation-avg">{stars || 0}</span>
            </div>
            <div className="text-sm text-muted-foreground mt-1" data-testid="reputation-count">
              {l.reviews_count || 0} reseña{(l.reviews_count || 0) === 1 ? "" : "s"}
            </div>
            {/* Distribución de estrellas visual */}
            <div className="mt-4 space-y-1">
              {[5,4,3,2,1].map((n) => {
                const count = (data.reviews || []).filter((r) => r.rating === n).length;
                const total = data.reviews?.length || 0;
                const pct = total > 0 ? Math.round((count / total) * 100) : 0;
                return (
                  <div key={n} className="flex items-center gap-2 text-[11px]" data-testid={`rating-bar-${n}`}>
                    <span className="w-6 text-muted-foreground">{n}★</span>
                    <div className="flex-1 h-1.5 rounded-full bg-white/5 overflow-hidden">
                      <div className="h-full bg-amber-400 transition-all" style={{ width: `${pct}%` }}/>
                    </div>
                    <span className="w-6 text-right text-muted-foreground">{count}</span>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="panel-glow p-6" data-testid="trust-panel">
            <div className="flex items-center gap-2">
              <ShieldCheck size={16} className="text-[hsl(var(--accent))]"/>
              <span className="label-eyebrow">Operación anónima</span>
            </div>
            <p className="text-sm text-muted-foreground mt-3 leading-relaxed">
              No publicamos correos ni teléfonos. Toda la coordinación, archivos y pagos viajan dentro de Colega Link con Pago Protegido.
            </p>
          </div>

          {l.domicilio_profesional && (
            <div className="panel p-6" data-testid="office-panel">
              <div className="label-eyebrow">Oficina profesional</div>
              <p className="text-sm text-foreground/85 mt-2 leading-relaxed">{l.domicilio_profesional}</p>
              <p className="text-[11px] text-muted-foreground mt-2">
                La dirección se comparte con la contraparte solo después del pago inicial (Pago Protegido).
              </p>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}
